import {ButtonLink} from '@components/ui/button-link/ButtonLink.tsx'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {FormCardSection} from '@components/ui/form-atoms/FormAtoms.tsx'
import {GridItem} from '@components/ui/grid-atoms/GridAtoms.tsx'
import {FemaleIcon, MaleIcon} from '@components/ui/icon/Icon.tsx'
import {InputHelpText} from '@components/ui/input-help-text/InputHelpText.tsx'
import {Skeleton} from '@components/ui/skeleton/Skeleton.tsx'
import {useTranslation} from '@/translations/i18n.tsx'
import {routes} from '@utilities/constants/routes.tsx'
import {getPatientServiceType, patientServiceTypeMapToLabel} from '@utilities/helpers.ts'
import {FC, useMemo} from 'react'
import {generatePath} from 'react-router-dom'
import {useTheme} from 'styled-components'
import {PatientExtended} from '@/features/patients/types'
import {useGetPatient} from '@/features/patients/services/useGetPatient'
import {Text} from '@/components/ui/text/Text'
import {usePermissions} from '@/features/auth/store/store'

interface PatientIdSectionProps {
    patientId: string
    patient?: undefined
}

interface PatientSectionProps {
    patientId?: undefined
    patient: Pick<
        PatientExtended,
        'id' | 'fullName' | 'gender' | 'birthDate' | 'residenceAslDistrict' | 'domicileAslDistrict'
    >
}

export const PatientSection: FC<PatientIdSectionProps | PatientSectionProps> = ({patientId, patient}) => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const patientQuery = useGetPatient({
        patientId,
        responseType: 'extended',
        options: {enabled: !patient && !!patientId}
    })

    const {palette} = useTheme()

    const birthDate = useMemo(() => {
        if (patient) return patient.birthDate
        if (patientQuery.data) return patientQuery.data.birthDate
        return null
    }, [patient, patientQuery.data])

    const yearsMonthsServiceType = useMemo(
        () => (birthDate ? getPatientServiceType(new Date(birthDate)) : null),
        [birthDate]
    )

    const patientData = useMemo(() => {
        if (patient) return patient
        if (patientQuery.data) return patientQuery.data
        return null
    }, [patient, patientQuery.data])

    return (
        <FormCardSection columns={3} padding={3}>
            {patientQuery.isLoading ? (
                <Skeleton height={23} />
            ) : patientQuery.isError ? (
                <InputHelpText error={t('errors:default')} />
            ) : (
                patientData && (
                    <>
                        <GridItem spanColumn={1}>
                            <Flexbox direction="column">
                                <Flexbox gap={1}>
                                    {permissions?.patients?.read ? (
                                        <ButtonLink
                                            variant="tertiary"
                                            shape="text"
                                            to={generatePath(routes.PATIENT_DETAILS_ROUTE.handle.path, {
                                                id: patientData.id
                                            })}
                                        >
                                            {patientData.fullName}
                                        </ButtonLink>
                                    ) : (
                                        <Text size="textSm" weight="500">
                                            {patientData.fullName}
                                        </Text>
                                    )}
                                    {patientData.gender == 'male' && (
                                        <MaleIcon size={18} color={palette.primary[600]} />
                                    )}
                                    {patientData.gender == 'female' && (
                                        <FemaleIcon size={18} color={palette.purple[600]} />
                                    )}
                                </Flexbox>
                                {yearsMonthsServiceType && (
                                    <Text size="textSm">
                                        {t(patientServiceTypeMapToLabel[yearsMonthsServiceType.serviceType])} (
                                        {yearsMonthsServiceType.years > 0
                                            ? `${t('commons:years_age', {count: yearsMonthsServiceType.years})}`
                                            : `${t('commons:months_age', {count: yearsMonthsServiceType.months})}`}
                                        )
                                    </Text>
                                )}
                            </Flexbox>
                        </GridItem>
                        <GridItem spanColumn={1} direction="column">
                            <Text size="textSm" palette="neutral" shade="500">
                                {t('commons:residence_asl')}
                            </Text>
                            <Text size="textSm">
                                {patientData.residenceAslDistrict?.aslName} - {patientData.residenceAslDistrict?.name}
                            </Text>
                        </GridItem>
                        <GridItem spanColumn={1} direction="column">
                            <Text size="textSm" palette="neutral" shade="500">
                                {t('commons:domicile_asl')}
                            </Text>
                            <Text size="textSm">
                                {patientData.domicileAslDistrict?.id == patientData.residenceAslDistrict?.id ? (
                                    <span style={{fontStyle: 'italic'}}>{t('commons:same_as_residence')}</span>
                                ) : (
                                    `${patientData.domicileAslDistrict?.aslName} - ${patientData.domicileAslDistrict?.name}`
                                )}
                            </Text>
                        </GridItem>
                    </>
                )
            )}
        </FormCardSection>
    )
}
